import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, IconButton, Collapse, CircularProgress, Alert, Button,
  Select, MenuItem, FormControl, InputLabel, TextField,
  Paper,
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowUp, CheckCircle, Cancel, HelpOutline,
} from '@mui/icons-material';
import { fetchSalesOrders, runAtpForSO, queryAtp } from '../../store/slices/supplyChainSlice';
import { SalesOrder } from '../../store/slices/supplyChainSlice';
import { AppDispatch, RootState } from '../../store/store';

const dayColor = (delta: number | null): any => {
  if (delta === null) return 'default';
  if (delta <= 0) return 'success';
  if (delta <= 7) return 'warning';
  return 'error';
};

const AtpIcon: React.FC<{ feasible: boolean | null }> = ({ feasible }) => {
  if (feasible === null) return <HelpOutline fontSize="small" color="disabled" />;
  return feasible ? <CheckCircle fontSize="small" color="success" /> : <Cancel fontSize="small" color="error" />;
};

const SORow: React.FC<{ so: SalesOrder; onRunAtp: (id: string) => void }> = ({ so, onRunAtp }) => {
  const [open, setOpen] = useState(false);
  const delta = so.committedShipDate && so.requestedShipDate
    ? Math.round((new Date(so.committedShipDate).getTime() - new Date(so.requestedShipDate).getTime()) / 86400000)
    : null;

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell><strong>{so.soNumber}</strong></TableCell>
        <TableCell>{so.customerName}</TableCell>
        <TableCell>
          <Chip label={so.priority} size="small" color={so.priority === 'high' ? 'error' : 'default'} />
        </TableCell>
        <TableCell><Chip label={so.status} size="small" /></TableCell>
        <TableCell>{so.requestedShipDate ? new Date(so.requestedShipDate).toLocaleDateString() : '—'}</TableCell>
        <TableCell>{so.committedShipDate ? new Date(so.committedShipDate).toLocaleDateString() : '—'}</TableCell>
        <TableCell>
          {delta !== null
            ? <Chip label={delta > 0 ? `+${delta}d` : `${delta}d`} size="small" color={dayColor(delta)} />
            : '—'}
        </TableCell>
        <TableCell><AtpIcon feasible={so.atpFeasible ?? null} /></TableCell>
        <TableCell>
          <Button size="small" variant="outlined" onClick={() => onRunAtp(so.id)}>Run ATP</Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Line Items</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item Code</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Ordered</TableCell>
                    <TableCell align="right">Shipped</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell>ATP Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(so.lines || []).map((line, i) => (
                    <TableRow key={line.id || i}>
                      <TableCell>{line.itemCode}</TableCell>
                      <TableCell>{line.itemDescription}</TableCell>
                      <TableCell align="right">{line.orderedQuantity}</TableCell>
                      <TableCell align="right">{line.shippedQuantity}</TableCell>
                      <TableCell>{line.requestedDate ? new Date(line.requestedDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{line.atpDate ? new Date(line.atpDate).toLocaleDateString() : '—'}</TableCell>
                      <TableCell><Chip label={line.lineStatus} size="small" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const AtpQueryPanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { atpQuery } = useSelector((s: RootState) => s.supplyChain);
  const [itemCode, setItemCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [requestedDate, setRequestedDate] = useState('');

  const handleCheck = () => {
    if (itemCode && quantity && requestedDate) {
      dispatch(queryAtp({ itemCode, quantity: Number(quantity), requestedDate }));
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Typography variant="h6" gutterBottom>ATP What-If Query</Typography>
      <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
        <TextField size="small" label="Item Code" value={itemCode} onChange={e => setItemCode(e.target.value)} />
        <TextField size="small" label="Quantity" type="number" value={quantity} onChange={e => setQuantity(e.target.value)} />
        <TextField size="small" label="Requested Date" type="date" InputLabelProps={{ shrink: true }} value={requestedDate} onChange={e => setRequestedDate(e.target.value)} />
        <Button variant="contained" onClick={handleCheck} disabled={atpQuery.loading}>
          {atpQuery.loading ? <CircularProgress size={20} /> : 'Check ATP'}
        </Button>
      </Box>
      {atpQuery.error && <Alert severity="error" sx={{ mt: 1 }}>{atpQuery.error}</Alert>}
      {atpQuery.result && (
        <Box mt={2}>
          <Chip
            label={atpQuery.result.feasible ? 'Feasible' : 'Not Feasible'}
            color={atpQuery.result.feasible ? 'success' : 'error'}
            sx={{ mr: 1 }}
          />
          <Typography variant="body2" component="span">
            ATP Date: <strong>{atpQuery.result.atpDate ? new Date(atpQuery.result.atpDate).toLocaleDateString() : '—'}</strong>
            {' | Days Delta: '}
            <strong>{atpQuery.result.daysDelta}</strong>
            {' | Constraint: '}
            <strong>{atpQuery.result.constraintReason}</strong>
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const SalesOrders: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { salesOrders } = useSelector((s: RootState) => s.supplyChain);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    dispatch(fetchSalesOrders(statusFilter ? { status: statusFilter } : {}));
  }, [dispatch, statusFilter]);

  const handleRunAtp = (soId: string) => {
    dispatch(runAtpForSO(soId));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Sales Orders</Typography>

      <Box sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pendingFulfillment">Pending Fulfillment</MenuItem>
            <MenuItem value="partiallyFulfilled">Partially Fulfilled</MenuItem>
            <MenuItem value="fullyFulfilled">Fully Fulfilled</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {salesOrders.error && <Alert severity="error" sx={{ mb: 2 }}>{salesOrders.error}</Alert>}
      {salesOrders.loading ? <CircularProgress /> : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>SO Number</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested Ship</TableCell>
              <TableCell>Committed Ship</TableCell>
              <TableCell>Delta</TableCell>
              <TableCell>ATP</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {salesOrders.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography color="textSecondary">No sales orders found</Typography>
                </TableCell>
              </TableRow>
            ) : salesOrders.data.map(so => <SORow key={so.id} so={so} onRunAtp={handleRunAtp} />)}
          </TableBody>
        </Table>
      )}

      <AtpQueryPanel />
    </Box>
  );
};

export default SalesOrders;
